import React, { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { useAuth } from "@/lib/auth";
import {
  BedIcon, CoinsIcon, DoorOpenIcon, BroomIcon, TrendUpIcon, CalendarCheckIcon,
  CrownSimpleIcon, MountainsIcon, FunnelIcon, PrinterIcon, WarningIcon,
} from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Cell } from "recharts";
import DayEndDialog from "@/pages/frontdesk/DayEndDialog";
import { MoonIcon } from "@phosphor-icons/react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// Preset date ranges
const RANGE_PRESETS = [
  { id: "today", label: "Today" },
  { id: "week", label: "Last 7 days" },
  { id: "mtd", label: "This month" },
  { id: "30d", label: "Last 30 days" },
  { id: "quarter", label: "This quarter" },
  { id: "ytd", label: "Year to date" },
  { id: "custom", label: "Custom" },
];

function computeRange(preset, custom) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
  switch (preset) {
    case "today": return { start: iso(today), end: iso(today) };
    case "week": return { start: iso(addDays(today, -6)), end: iso(today) };
    case "30d": return { start: iso(addDays(today, -29)), end: iso(today) };
    case "mtd": return { start: iso(new Date(today.getFullYear(), today.getMonth(), 1)), end: iso(today) };
    case "quarter": {
      const q = Math.floor(today.getMonth() / 3);
      return { start: iso(new Date(today.getFullYear(), q * 3, 1)), end: iso(today) };
    }
    case "ytd": return { start: iso(new Date(today.getFullYear(), 0, 1)), end: iso(today) };
    case "custom": return { start: custom.start, end: custom.end };
    default: return { start: iso(addDays(today, -6)), end: iso(today) };
  }
}

function KPI({ icon: Icon, label, value, hint, tone = "primary" }) {
  const toneClasses = {
    primary: "text-primary",
    success: "text-[hsl(var(--success))]",
    warning: "text-[hsl(var(--warning))]",
    danger: "text-destructive",
  };
  return (
    <Card className="p-6 relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div>
          <p className="label-overline">{label}</p>
          <p className="font-display text-4xl tracking-tight mt-3">{value}</p>
          {hint && <p className="text-xs text-muted-foreground mt-2">{hint}</p>}
        </div>
        <div className={`p-2 rounded-md bg-muted ${toneClasses[tone]}`}>
          <Icon size={22} weight="duotone" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { activeId, active, properties, setActive } = useProperty();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [byProp, setByProp] = useState([]);
  const [brand, setBrand] = useState({ group_name: "The King's Group of Hotels", tagline: "Heritage hospitality of the Sikkim Himalayas" });
  const [expSummary, setExpSummary] = useState({ total: 0, gst_input_claimable: 0, by_category: {} });
  const [preset, setPreset] = useState("mtd");
  const [custom, setCustom] = useState(() => {
    const iso = (d) => d.toISOString().slice(0, 10);
    return { start: iso(new Date(Date.now() - 6 * 86400000)), end: iso(new Date()) };
  });
  const [scope, setScope] = useState("active"); // 'active' | 'all' | property.id
  const [dayEndOpen, setDayEndOpen] = useState(false);
  const [pendingDayEnd, setPendingDayEnd] = useState([]);
  const [dayEndPrefill, setDayEndPrefill] = useState(null);

  const range = useMemo(() => computeRange(preset, custom), [preset, custom]);

  const scopedPropertyId = useMemo(() => {
    if (scope === "all") return null;
    if (scope === "active") return activeId;
    return scope;
  }, [scope, activeId]);

  useEffect(() => {
    const params = { start: range.start, end: range.end };
    if (scopedPropertyId) params.property_id = scopedPropertyId;
    api.get("/reports/overview", { params }).then((r) => setData(r.data));
    api.get("/reports/occupancy-by-property").then((r) => setByProp(r.data));
    // Expense summary uses YYYY-MM (month) — pick range.end's month
    const month = range.end.slice(0, 7);
    api.get("/expenses/summary", { params: { month }, silent: true }).then((r) => setExpSummary(r.data)).catch(() => {});
  }, [scopedPropertyId, range.start, range.end]);

  useEffect(() => {
    api.get("/settings/platform/public").then((r) => r.data && setBrand({
      group_name: r.data.group_name || brand.group_name,
      tagline: r.data.tagline || brand.tagline,
    })).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for any past business day that wasn't closed → nag banner
  useEffect(() => {
    const load = () => {
      const params = { lookback_days: 7 };
      if (scopedPropertyId) params.property_id = scopedPropertyId;
      api.get("/day-end/pending", { params }).then((r) => setPendingDayEnd(r.data || [])).catch(() => {});
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000); // re-check every 5 min
    return () => clearInterval(t);
  }, [scopedPropertyId]);

  const onScopeChange = (v) => {
    setScope(v);
    // If picking a specific property, also sync the active property switcher
    if (v !== "all" && v !== "active") setActive?.(v);
  };

  const overdue = pendingDayEnd.filter((p) => (p.hours_overdue || 0) >= 24);
  const dueSoon = pendingDayEnd.filter((p) => (p.hours_overdue || 0) < 24);
  const openDayEndFor = (item) => {
    if (item) {
      setDayEndPrefill({ property_id: item.property_id, date: item.date });
      if (item.property_id && item.property_id !== activeId) setActive?.(item.property_id);
    } else {
      setDayEndPrefill(null);
    }
    setDayEndOpen(true);
  };

  return (
    <div>
      {/* Day End nag banner — alerts if any past business day is still open */}
      {(overdue.length > 0 || dueSoon.length > 0) && (
        <div
          className={`mb-4 rounded-xl border p-4 flex flex-col md:flex-row md:items-center gap-3 ${overdue.length ? "bg-destructive/10 border-destructive/40" : "bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/40"}`}
          data-testid="dashboard-dayend-alert"
        >
          <div className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${overdue.length ? "bg-destructive/20 text-destructive" : "bg-[hsl(var(--warning))]/20 text-[hsl(var(--warning))]"}`}>
            <WarningIcon size={22} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-lg leading-tight">
              {overdue.length > 0
                ? `Day End overdue — ${overdue.length} day${overdue.length > 1 ? "s" : ""} not closed within 24h`
                : `Day End pending — close ${dueSoon.length} day${dueSoon.length > 1 ? "s" : ""} before the 24h window`}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {[...overdue, ...dueSoon].slice(0, 4).map((p) => (
                <span key={`${p.property_id}-${p.date}`} className="inline-flex items-center gap-1 mr-3">
                  <span className="font-mono-plex">{p.date}</span>
                  <span className="text-foreground">· {p.property_name}</span>
                  <span className="font-mono-plex text-[10px] uppercase tracking-widest">({p.hours_overdue}h)</span>
                </span>
              ))}
              {(overdue.length + dueSoon.length) > 4 && (
                <span className="italic">+ {(overdue.length + dueSoon.length) - 4} more…</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => openDayEndFor([...overdue, ...dueSoon][0])}
              data-testid="dashboard-dayend-alert-close-now"
            >
              <MoonIcon size={14} className="mr-1" weight="duotone" /> Close oldest now
            </Button>
          </div>
        </div>
      )}

      {/* Heritage hero band */}
      <div className="flex justify-end mb-2 gap-2 no-print">
        <Button size="sm" onClick={() => setDayEndOpen(true)} data-testid="dashboard-day-end-btn">
          <MoonIcon size={14} className="mr-1" weight="duotone" /> Day End
        </Button>
        <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="dashboard-print-btn">
          <PrinterIcon size={14} className="mr-1" /> Print / export dashboard
        </Button>
      </div>
      <div className="relative overflow-hidden rounded-xl border border-border mb-8 bg-primary text-primary-foreground">
        <img
          src="https://images.pexels.com/photos/36827299/pexels-photo-36827299.jpeg?auto=compress&cs=tinysrgb&h=500&w=1600"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/55" />
        <div className="grain absolute inset-0" />
        <div className="relative z-10 p-8 md:p-10 min-h-[220px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full border-2 border-gold/80 flex items-center justify-center text-gold bg-primary/40 shadow-inner">
              <CrownSimpleIcon size={18} weight="duotone" />
            </div>
            <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-gold" data-testid="dashboard-hero-group">
              {brand.group_name}
            </p>
          </div>
          <div className="mt-6">
            {active && (
              <div className="mb-3 flex items-center gap-3 flex-wrap" data-testid="dashboard-hero-property">
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-none tracking-tight text-gold drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]">
                  {active.name}
                </h2>
                <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/95 border border-gold/60 rounded-full px-3 py-1 bg-primary/60 backdrop-blur-sm">
                  <MountainsIcon size={12} weight="duotone" /> {active.city}, {active.state}
                </span>
              </div>
            )}
            <h1 className="font-display text-2xl md:text-3xl leading-tight tracking-tight max-w-3xl text-white">
              {greeting()}, <em className="text-gold not-italic">{user?.name?.split(" ")[0] || "there"}</em>.
            </h1>
            <div className="flex items-center gap-3 mt-3">
              <div className="prayer-flags w-20 opacity-90" />
              <p className="text-sm text-white/90">
                {active ? active.address : brand.tagline}
              </p>
            </div>
          </div>
        </div>
      </div>

      {!properties.length ? (
        <Card className="p-10 text-center">
          <MountainsIcon size={40} className="mx-auto text-muted-foreground" weight="duotone" />
          <h3 className="font-display text-xl mt-4">No properties yet</h3>
          <p className="text-sm text-muted-foreground mt-1">Head to Properties to add your first hotel.</p>
        </Card>
      ) : (
        <></>
      )}
      {properties.length > 0 && (
        <>
          {/* Filter bar */}
          <Card className="p-4 mb-6" data-testid="dashboard-filter-bar">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <FunnelIcon size={16} weight="duotone" />
                <span className="label-overline">Filters</span>
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3 min-w-[240px]">
                <div>
                  <label className="text-xs text-muted-foreground">Property</label>
                  <Select value={scope} onValueChange={onScopeChange}>
                    <SelectTrigger className="mt-1" data-testid="dashboard-property-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active: {active?.name || "—"}</SelectItem>
                      <SelectItem value="all">All properties (portfolio)</SelectItem>
                      {properties.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Date range</label>
                  <Select value={preset} onValueChange={setPreset}>
                    <SelectTrigger className="mt-1" data-testid="dashboard-range-preset"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RANGE_PRESETS.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  {preset === "custom" ? (
                    <>
                      <div className="flex-1">
                        <label className="text-xs text-muted-foreground">From</label>
                        <input
                          type="date"
                          value={custom.start}
                          onChange={(e) => setCustom({ ...custom, start: e.target.value })}
                          className="w-full h-10 rounded-md border border-input bg-background px-3 py-1 text-sm mt-1"
                          data-testid="dashboard-custom-start"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs text-muted-foreground">To</label>
                        <input
                          type="date"
                          value={custom.end}
                          onChange={(e) => setCustom({ ...custom, end: e.target.value })}
                          className="w-full h-10 rounded-md border border-input bg-background px-3 py-1 text-sm mt-1"
                          data-testid="dashboard-custom-end"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-muted-foreground pb-3" data-testid="dashboard-range-summary">
                      <span className="font-mono-plex">{range.start}</span> → <span className="font-mono-plex">{range.end}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <KPI icon={TrendUpIcon} label="Occupancy" value={`${data?.occupancy_pct ?? 0}%`} hint={`${data?.occupied ?? 0} of ${data?.total_rooms ?? 0} rooms occupied`} tone="primary" />
            <KPI icon={CoinsIcon} label={preset === "mtd" ? "Revenue MTD" : "Revenue"} value={inr(data?.revenue_mtd)} hint={`ADR ${inr(data?.adr)} · ${range.start} → ${range.end}`} tone="success" />
            <KPI icon={CalendarCheckIcon} label="Today's Arrivals" value={data?.todays_checkins ?? 0} hint={`${data?.todays_checkouts ?? 0} departures`} tone="warning" />
            <KPI icon={BroomIcon} label="Cleaning Queue" value={data?.cleaning ?? 0} hint={`${data?.available ?? 0} available now`} tone="danger" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            <Card className="p-6 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="label-overline">Revenue trend</p>
                  <h3 className="font-display text-xl mt-1">{range.start} → {range.end}</h3>
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data?.revenue_trend || []} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => inr(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} />
                    <Line type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-6">
              <p className="label-overline">Portfolio</p>
              <h3 className="font-display text-xl mt-1">Occupancy by property</h3>
              <ul className="mt-4 space-y-4">
                {byProp.map((p) => (
                  <li key={p.property_id}>
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="font-medium truncate">{p.name}</span>
                      <span className="font-mono-plex text-xs text-muted-foreground">
                        {p.occupied}/{p.total} · {p.occupancy_pct}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full mt-2 overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${p.occupancy_pct}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <Card className="p-6 flex items-center gap-4">
              <DoorOpenIcon size={28} weight="duotone" className="text-primary" />
              <div><p className="label-overline">Available</p><p className="font-display text-2xl">{data?.available ?? 0}</p></div>
            </Card>
            <Card className="p-6 flex items-center gap-4">
              <BedIcon size={28} weight="duotone" className="text-secondary" />
              <div><p className="label-overline">Occupied</p><p className="font-display text-2xl">{data?.occupied ?? 0}</p></div>
            </Card>
            <Card className="p-6 flex items-center gap-4">
              <BroomIcon size={28} weight="duotone" className="text-[hsl(var(--warning))]" />
              <div><p className="label-overline">Cleaning</p><p className="font-display text-2xl">{data?.cleaning ?? 0}</p></div>
            </Card>
          </div>

          {/* Expense insights */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            <Card className="p-6 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="label-overline">Cost book · this month</p>
                  <h3 className="font-display text-xl mt-1">Expenses vs revenue</h3>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Net (revenue − expenses)</p>
                  <p className="font-display text-xl" data-testid="dashboard-net-figure">
                    {inr((data?.revenue_mtd || 0) - (expSummary.total || 0))}
                  </p>
                </div>
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      { name: "Revenue MTD", amount: data?.revenue_mtd || 0, tone: "primary" },
                      ...Object.entries(expSummary.by_category || {}).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([k, v]) => ({ name: k.replace("_", " "), amount: v, tone: "expense" })),
                    ]}
                    margin={{ top: 8, right: 8, bottom: 0, left: -10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} interval={0} tickFormatter={(s) => s.charAt(0).toUpperCase() + s.slice(1)} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => inr(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} />
                    <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                      {[
                        { tone: "primary" },
                        ...Object.entries(expSummary.by_category || {}).sort((a, b) => b[1] - a[1]).slice(0, 6).map(() => ({ tone: "expense" })),
                      ].map((e, i) => (
                        <Cell key={i} fill={e.tone === "primary" ? "hsl(var(--primary))" : "hsl(var(--warning))"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className="p-6">
              <p className="label-overline">Cost breakdown</p>
              <h3 className="font-display text-xl mt-1">Top categories</h3>
              <ul className="mt-4 space-y-3">
                {Object.entries(expSummary.by_category || {}).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([k, v]) => {
                  const pct = expSummary.total > 0 ? Math.round((v / expSummary.total) * 100) : 0;
                  return (
                    <li key={k}>
                      <div className="flex justify-between items-baseline text-sm">
                        <span className="font-medium capitalize">{k.replace("_", " ")}</span>
                        <span className="font-mono-plex text-xs text-muted-foreground">{inr(v)} · {pct}%</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full mt-2 overflow-hidden">
                        <div className="h-full bg-[hsl(var(--warning))]" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
                {Object.keys(expSummary.by_category || {}).length === 0 && (
                  <li className="text-sm text-muted-foreground italic">No expenses recorded yet this month.</li>
                )}
              </ul>
              <div className="mt-4 pt-4 border-t border-border flex justify-between text-sm">
                <span className="text-muted-foreground">Input GST claimable</span>
                <span className="font-mono-plex text-secondary">{inr(expSummary.gst_input_claimable)}</span>
              </div>
            </Card>
          </div>
        </>
      )}
      <DayEndDialog
        open={dayEndOpen}
        onClose={() => { setDayEndOpen(false); setDayEndPrefill(null); }}
        prefill={dayEndPrefill}
      />
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "Good night";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export function StatusBadge({ status }) {
  const map = {
    available: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
    occupied: "bg-secondary/15 text-secondary border-secondary/30",
    cleaning: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
    maintenance: "bg-destructive/15 text-destructive border-destructive/30",
  };
  return <Badge variant="outline" className={map[status] || ""}>{status}</Badge>;
}
